import React from 'react'
import styled from '@emotion/styled'
import { Location } from '@reach/router'
import { keyframes } from '@emotion/core'
import { StaticQuery, graphql, Link } from 'gatsby'
import ThemeConsumer from '@/hoc/ThemeConsumer'
import { zIndex, easing, layouts, fonts, lang } from '@/utils/preset'
import { tablet, mobile, mobileS } from '@/utils/media'
import { mq, rem } from '@/utils/helpers'
import Nav from './Nav'
import Logo from '@/components/svg/Logo'
import { AnchorScroll } from '@/hooks/useAnchorScroll.js'

class Header extends React.Component {
  constructor (props) {
    super(props)
    this.state = {
      showNav: false,
      scrollTop: 0
    }
    this.rootRef = React.createRef()
    if (typeof window === 'undefined') return
    window.addEventListener('scroll', this.onScrollWindow)
    window.addEventListener('resize', this.onResizeWindow)
  }
  componentDidMount = () => {
    this.updateHeight()
  }
  componentWillUnmount = () => {
    window.removeEventListener('resize', this.onResizeWindow)
    window.removeEventListener('scroll', this.onScrollWindow)
  }
  onClickMenu = () => {
    this.setState({
      ...this.state,
      showNav: true
    })
  }
  closeNav = () => {
    this.setState({
      ...this.state,
      showNav: false
    })
  }
  onResizeWindow = () => {
    this.updateHeight()
  }
  updateHeight = () => {
    if (!this.rootRef.current) return
    const height = this.rootRef.current.clientHeight
    // ヘッダーの height ピッタリだと上のセクションが微妙に見える時があるので 2px 減らす
    document.documentElement.style.setProperty('--scroll-offset', `${height - 2}px`)
  }
  onScrollWindow = () => {
    this.setState({
      ...this.state,
      scrollTop: this.getScrollTop()
    })
  }
  getScrollTop () {
    if (typeof window === 'undefined') return 0
    const w = window
    return window.self === window
      ? w.pageYOffset
      : w.scrollTop
  }
  render () {
    const logoColor = this.state.scrollTop >= 1 ? 'contrast' : this.props.color
    const page = this.props.page
    const fixed = this.state.scrollTop >= 1
    return (
      <Location>
        {({ location }) => {
          const { homeLink, langsMenu, selectedLang, intl } = lang(this.props.lang)
          const langKey = selectedLang.langKey
          return (
            <StaticQuery
              query={query}
              render={ data => {
                const pathPrefix = data.site.pathPrefix
                return (
                  <AnchorScroll onBeforeScroll={this.closeNav} >
                    <Root className={fixed ? 'is-fixed' : false} ref={this.rootRef}>
                      <Container>
                        <Col>
                          <LogoButton to={`${homeLink}#top`}>
                            <Logo color={logoColor} />
                          </LogoButton>
                        </Col>
                        <Col>
                          <Lang>
                            {langsMenu.map((lang, index) => {
                              let to = location.pathname.replace(pathPrefix, '').replace(selectedLang.link, lang.link)
                              return (
                                <Lang_Item color={logoColor} page={page} key={index}>
                                  <Lang_Link active={lang.selected.toString()} color={logoColor} page={page} to={to}>{lang.langKey.toUpperCase()}</Lang_Link>
                                </Lang_Item>
                              )
                            })}
                          </Lang>
                          <Button theme={this.props.theme} href={`https://www.konoike.net${'/'.replace('/', selectedLang.link)}`} target="_blank">
                            <Button_Content>
                              <Button_Icon width="1269" height="1024" viewBox="0 0 1269 1024" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
                                <path d="M114.224 240.75H0V936.972C0 984.838 51.4008 1024 114.224 1024H1028.02V936.972H114.224V240.75Z"/>
                                <path fillRule="evenodd" clipRule="evenodd" d="M199.828 131.965C199.828 50.4425 278.776 0 353.55 0H1115.04C1189.82 0 1268.77 50.4425 1268.77 131.965V741.16C1268.77 822.682 1189.82 873.125 1115.04 873.125H353.55C278.776 873.125 199.828 822.682 199.828 741.16V131.965ZM353.55 89.8749C308.661 89.8749 289.703 117.757 289.703 131.965V741.16C289.703 755.368 308.661 783.25 353.55 783.25H1115.04C1159.93 783.25 1178.89 755.368 1178.89 741.16V131.965C1178.89 117.757 1159.93 89.8749 1115.04 89.8749H353.55Z"/>
                              </Button_Icon>
                              <Button_Text>{intl('Corporate Site', langKey)}</Button_Text>
                            </Button_Content>
                          </Button>
                          <MenuButton theme={this.props.theme} onClick={this.onClickMenu}>
                            <MenuButton_Line />
                            <MenuButton_Line />
                            <MenuButton_Line />
                          </MenuButton>
                        </Col>
                      </Container>
                    </Root>
                    <Nav show={this.state.showNav} onClose={this.closeNav} lang={this.props.lang} headerHeight={this.state.height} />
                  </AnchorScroll>
                )
              }}
            />
          )
        }}
      </Location>
    )
  }
}

const anim = {
  button: {
    color: (color1, color2) => keyframes({
      '50%': {
        color: color1
      },
      '100%': {
        color: color2
      },
      label: 'button-color'
    }),
    before: keyframes({
      '50%': {
        transform: 'translateX(100%)'
      },
      '100%': {
        transform: 'translateX(100%)'
      },
      label: 'button-after'
    }),
    after: keyframes({
      '0%': {
        transform: 'translateX(-100%)'
      },
      '50%': {
        transform: 'translateX(-100%)'
      },
      '100%': {
        transform: 'translateX(0%)'
      },
      label: 'button-after'
    })
  },
  line: {
    before: keyframes({
      '0%': {
        transform: 'translateX(0%)'
      },
      '50%': {
        transform: 'translateX(100%)'
      },
      '100%': {
        transform: 'translateX(100%)'
      },
      label: 'line-before'
    }),
    after: (x) => keyframes({
      '0%': {
        transform: 'translateX(-100%)'
      },
      '50%': {
        transform: 'translateX(-100%)'
      },
      '100%': {
        transform: `translateX(${x})`
      },
      label: 'line-after'
    })
  }
}

const Container = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 auto;
  width: 100%;
`

const Col = styled.div`
  display: flex;
  align-items: center;
`

const LogoButton = styled(Link)`
  display: block;
  width: ${rem(200)};
  transition: transform .2s ${easing.css.easeInOutCirc};
  transform-origin: left center;
  @media ${mq.and(tablet, mobile)} {
    width: ${rem(100)};
  }
`

const Lang = styled.div`
`

const Lang_Item = styled.span`
  &:not(:nth-of-type(1))::before {
    content: ' / ';
    font-family: ${fonts.en};
    font-size: ${rem(20)};
    transition: color .3s ease;
    color: ${props => {
      if (props.color === 'base') {
        if (props.page === 'interview') {
          return props.active === 'true' ? '#000' : 'rgba(0, 0, 0, 0.6)'
        } else {
          return props.active === 'true' ? '#fff' : 'rgba(255, 255, 255, 0.6)'
        }
      } else {
        return props.active === 'true' ? '#000' : 'rgba(0, 0, 0, 0.6)'
      }
    }};
  }
`

const Lang_Link = styled(Link)`
  color: ${props => {
    if (props.color === 'base') {
      if (props.page === 'interview') {
        return props.active === 'true' ? '#000' : 'rgba(0, 0, 0, 0.6)'
      } else {
        return props.active === 'true' ? '#fff' : 'rgba(255, 255, 255, 0.6)'
      }
    } else {
      return props.active === 'true' ? '#000' : 'rgba(0, 0, 0, 0.6)'
    }
  }};
  transition: color .3s ease;
  font-family: ${fonts.en};
  font-weight: ${props => props.active === 'true' ? 'bold' : 'normal'};
  font-size: ${rem(20)};
  @media ${mq.and(mobile)} {
    font-size: ${rem(16)};
  }
`

const Button = styled.a`
  margin-left: ${rem(30)};
  position: relative;
  display: block;
  width: ${rem(230)};
  height: ${rem(50)};
  color: ${props => props.theme.text.contrast.base};
  background-color: ${props => props.theme.text.contrast.base};;
  overflow: hidden;
  &:before,
  &:after {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: ${props => props.theme.text.base};
  }
  &:after {
    transform: translateX(-100%);
  }
  @media (hover: hover), (-ms-high-contrast: none) {
    &:hover {
      animation: ${props => anim.button.color(props.theme.text.base, props.theme.text.contrast.base)} 0.5s ${easing.css.easeInOutExpo};
    }
    &:hover:before {
      animation: ${anim.button.before} 0.5s ${easing.css.easeInOutExpo};
    }
    &:hover:after {
      animation: ${anim.button.after} 0.5s ${easing.css.easeInOutExpo};
    }
  }
  @media ${mq.and(tablet, mobile)} {
    display: none;
  }
`

const Button_Content = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  font-size: ${rem(14)};
`

const Button_Icon = styled.svg`
  width: ${rem(19.8281249992)};
  height: ${rem(16)};
`

const Button_Text = styled.span`
  margin-left: ${rem(14)};
  transform: translateY(-10%);
`

const MenuButton_Line = styled.div`
  position: relative;
  z-index: 2;
  height: 2px;
  width: 100%;
  overflow: hidden;

  &:before {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: currentColor;
    transform: translateX(0%);
  }
  &:after {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: currentColor;
    transform: translateX(-100%);
    transform-origin: right center;
  }
`

const MenuButton = styled.div`
  cursor: pointer;
  position: relative;
  margin-left: ${rem(30)};
  width: ${rem(66)};
  height: ${rem(50)};
  background-color: ${props => props.theme.text.base};
  color: ${props => props.theme.text.base};
  padding: ${rem(14)} ${rem(18)};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow: hidden;
  @media ${mq.and(tablet, mobile)} {
    width: ${rem(37)};
    height: ${rem(28)};
    padding: ${rem(8)} ${rem(10)};
  }
  @media ${mq.and(mobile)} {
    margin-left: ${rem(16)};
  }

  &:before,
  &:after {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: ${props => props.theme.text.contrast.base};
  }
  &:after {
    transform: translateX(-100%);
  }
  @media (hover: hover), (-ms-high-contrast: none) {
    &:hover {
      animation: ${props => anim.button.color(props.theme.text.contrast.base, props.theme.text.base)} 0.5s ${easing.css.easeInOutExpo};
    }
    &:hover:before {
      animation: ${anim.button.before} 0.5s ${easing.css.easeInOutExpo};
    }
    &:hover:after {
      animation: ${anim.button.after} 0.5s ${easing.css.easeInOutExpo};
    }
    &:hover ${MenuButton_Line} {
      &:nth-of-type(1):before {
        animation: ${anim.line.before} 0.5s ${easing.css.easeInOutExpo};
      }
      &:nth-of-type(2):before {
        animation: ${anim.line.before} 0.5s ${easing.css.easeInOutExpo} 0.05s;
      }
      &:nth-of-type(3):before {
        animation: ${anim.line.before} 0.5s ${easing.css.easeInOutExpo} 0.1s;
      }
      &:nth-of-type(1):after {
        animation: ${anim.line.after('0%')} 0.5s ${easing.css.easeInOutExpo};
      }
      &:nth-of-type(2):after {
        animation: ${anim.line.after('25%')} 0.5s ${easing.css.easeInOutExpo} 0.05s;
      }
      &:nth-of-type(3):after {
        animation: ${anim.line.after('50%')} 0.5s ${easing.css.easeInOutExpo} 0.1s;
      }
      &:nth-of-type(1):before,
      &:nth-of-type(2):before,
      &:nth-of-type(3):before,
      &:nth-of-type(1):after,
      &:nth-of-type(2):after,
      &:nth-of-type(3):after {
        animation-fill-mode: forwards;
      }
    }
  }
`

const Root = styled.header`
  position: fixed;
  will-change: transform;
  transform: translate3d(0, 0, 0);
  top: 0;
  left: 0;
  right: 0;
  padding: 0 ${rem(20)};
  height: ${rem(77)};
  z-index: ${zIndex.header.base};
  display: flex;
  align-items: center;
  background-color: #fff;
  transition:
    transform .2s ${easing.css.easeInOutCirc};
  @media ${mq.and(tablet, mobile)} {
    height: ${rem(48)};
    top: 0;
    padding: 0 ${rem(10)};
  }
  @media ${mq.and(mobileS)} {
    padding: ${rem(layouts.defs.pad.mobileS / 2)};
  }
`

export default ThemeConsumer(Header)

const query = graphql`
  query HeaderQuery {
    site {
      pathPrefix
    }
  }
`
